import * as React from "react";

import { LOGIN_FORM_WINDOW_EVENTS, useLoginForm_asController } from "../login-form-provider";

import { useOneTimePasswordUserExperience_asController } from "./one-time-password-user-experience-provider";

interface OneTimePasswordFieldSuggestionConsumerInterface {
  handleStartTheUserFlowForAOneTimeCodeBasedLogin: () => void;
}
const defaultValue =
  {} as unknown as OneTimePasswordFieldSuggestionConsumerInterface;
const OneTimePasswordFieldSuggestionContext = React.createContext(defaultValue);

interface OneTimePasswordFieldSuggestion_ControllerConsumerInterface {
  showOneTimePasswordSuggestionOnPasswordField: () => void;
}
const defaultValue_controller =
  {} as unknown as OneTimePasswordFieldSuggestion_ControllerConsumerInterface;
const OneTimePasswordFieldSuggestion_ControllerContext = React.createContext(
  defaultValue_controller
);

export const OneTimePasswordFieldSuggestionCustomerErrorMessageToLookFor =
  "sign-in-with-a-one-time-code-link";

export const OneTimePasswordFieldSuggestionProvider: React.FunctionComponent = (props) => {
  const { injectPasswordFieldError } = useLoginForm_asController();

  const {
    handleStartTheUserFlowForAOneTimeCodeBasedLogin:
      oneTimePasswordUserExperience_handleStartTheUserFlowForAOneTimeCodeBasedLogin,
  } = useOneTimePasswordUserExperience_asController();

  const showOneTimePasswordSuggestionOnPasswordField = React.useCallback(() => {
    injectPasswordFieldError(
      OneTimePasswordFieldSuggestionCustomerErrorMessageToLookFor
    );
  }, [injectPasswordFieldError]);

  const handleLoginFailed = React.useCallback(() => {
    showOneTimePasswordSuggestionOnPasswordField();
  }, [showOneTimePasswordSuggestionOnPasswordField]);

  const handleStartTheUserFlowForAOneTimeCodeBasedLogin =
    React.useCallback(() => {
      oneTimePasswordUserExperience_handleStartTheUserFlowForAOneTimeCodeBasedLogin();
    }, [
      oneTimePasswordUserExperience_handleStartTheUserFlowForAOneTimeCodeBasedLogin,
    ]);

  const value: OneTimePasswordFieldSuggestionConsumerInterface = React.useMemo(
    () => ({
      handleStartTheUserFlowForAOneTimeCodeBasedLogin,
    }),
    [handleStartTheUserFlowForAOneTimeCodeBasedLogin]
  );
  const controllerValue: OneTimePasswordFieldSuggestion_ControllerConsumerInterface =
    React.useMemo(
      () => ({
        showOneTimePasswordSuggestionOnPasswordField,
      }),
      [showOneTimePasswordSuggestionOnPasswordField]
    );

  React.useEffect(() => {
    window.addEventListener(LOGIN_FORM_WINDOW_EVENTS.LOGIN_FAILED, handleLoginFailed);
    return () => {
      window.removeEventListener(LOGIN_FORM_WINDOW_EVENTS.LOGIN_FAILED, handleLoginFailed);
    };
  }, [handleLoginFailed]);

  return (
    <OneTimePasswordFieldSuggestionContext.Provider value={value}>
      <OneTimePasswordFieldSuggestion_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </OneTimePasswordFieldSuggestion_ControllerContext.Provider>
    </OneTimePasswordFieldSuggestionContext.Provider>
  );
};

export const useOneTimePasswordFieldSuggestion = () => {
  const value = React.useContext(OneTimePasswordFieldSuggestionContext);

  if (value === defaultValue) {
    throw new Error(
      "useOneTimePasswordFieldSuggestion must be used within a OneTimePasswordFieldSuggestionProvider"
    );
  }

  return value;
};

export const useOneTimePasswordFieldSuggestion_asController = () => {
  const value = React.useContext(
    OneTimePasswordFieldSuggestion_ControllerContext
  );

  if (value === defaultValue_controller) {
    throw new Error(
      "useOneTimePasswordFieldSuggestion_asController must be used within a OneTimePasswordFieldSuggestionProvider"
    );
  }

  return value;
};
